import getOption, { OPTION_ALL_VALUE } from 'lib/getOption'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

function firstBlockFilter(item, filters) {
  if (
    item.accepted < filters.acceptedMin ||
    item.accepted > filters.acceptedMax
  )
    return false

  if (
    item.acceptedWithReprice < filters.acceptedWithRepriceMin ||
    item.acceptedWithReprice > filters.acceptedWithRepriceMax
  )
    return false

  if (
    item.notChosen < filters.notChosenMin ||
    item.notChosen > filters.notChosenMax
  )
    return false

  if (
    [item.partnerId, OPTION_ALL_VALUE].every(
      (partner) => !filters.partners.includes(partner)
    )
  )
    return false

  return true
}

const useSummaryFilters = () => {
  const { t } = useTranslation()

  const allOption = useMemo(
    () => getOption(t('common.form.all'), OPTION_ALL_VALUE),
    [t]
  )

  const defaultFilters = useMemo(
    () => ({
      acceptedMin: 0,
      acceptedMax: 0,
      acceptedWithRepriceMin: 0,
      acceptedWithRepriceMax: 0,
      notChosenMin: 0,
      notChosenMax: 0,
      partners: [OPTION_ALL_VALUE],
    }),
    []
  )

  const [filters, setFilters] = useState({
    ...defaultFilters,
    initialized: false,
  })

  const [filtersData, setFiltersData] = useState({
    acceptedMin: 0,
    acceptedMax: 0,
    acceptedWithRepriceMin: 0,
    acceptedWithRepriceMax: 0,
    notChosenMin: 0,
    notChosenMax: 0,
    partners: [allOption],
  })

  const resetFilters = useCallback(() => {
    setFilters((s) => ({
      ...s,
      ...defaultFilters,
      acceptedMin: filtersData.acceptedMin,
      acceptedMax: filtersData.acceptedMax,
      acceptedWithRepriceMin: filtersData.acceptedWithRepriceMin,
      acceptedWithRepriceMax: filtersData.acceptedWithRepriceMax,
      notChosenMin: filtersData.notChosenMin,
      notChosenMax: filtersData.notChosenMax,
    }))
  }, [
    defaultFilters,
    filtersData.acceptedMax,
    filtersData.acceptedMin,
    filtersData.acceptedWithRepriceMax,
    filtersData.acceptedWithRepriceMin,
    filtersData.notChosenMax,
    filtersData.notChosenMin,
  ])

  const initFilters = useCallback(
    async (partners) => {
      const acceptedMax = Math.max(
        ...partners.map((partner) => partner.accepted)
      )

      const acceptedWithRepriceMax = Math.max(
        ...partners.map((partner) => partner.acceptedWithReprice)
      )

      const notChosenMax = Math.max(
        ...partners.map((partner) => partner.notChosen)
      )

      setFiltersData((s) => ({
        ...s,
        acceptedMax,
        acceptedWithRepriceMax,
        notChosenMax,
        partners: [
          allOption,
          ...partners.map((partner) =>
            getOption(partner.name, partner.partnerId)
          ),
        ],
      }))

      setFilters({
        ...defaultFilters,
        acceptedMax,
        acceptedWithRepriceMax,
        notChosenMax,
        initialized: true,
      })
    },
    [allOption, defaultFilters]
  )

  const setAcceptedRange = useCallback(
    (acceptedMin, acceptedMax) => {
      setFilters((s) => ({ ...s, acceptedMin, acceptedMax }))
    },
    [setFilters]
  )

  const setAcceptedWithRepriceRange = useCallback(
    (acceptedWithRepriceMin, acceptedWithRepriceMax) => {
      setFilters((s) => ({
        ...s,
        acceptedWithRepriceMin,
        acceptedWithRepriceMax,
      }))
    },
    [setFilters]
  )

  const setNotChosenRange = useCallback(
    (notChosenMin, notChosenMax) => {
      setFilters((s) => ({ ...s, notChosenMin, notChosenMax }))
    },
    [setFilters]
  )

  const setPartners = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, partners: value }))
    },
    [setFilters]
  )

  const filter = useCallback((items, filters) => {
    return items.filter((item) => {
      if (!firstBlockFilter(item, filters)) return false

      return true
    })
  }, [])

  return {
    filters,
    filtersData,
    initFilters,
    setAcceptedRange,
    setAcceptedWithRepriceRange,
    setNotChosenRange,
    setPartners,
    filter,
    resetFilters,
    initialized: filters.initialized,
  }
}
export default useSummaryFilters
